import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { Moon, Sun } from "lucide-react";
import { exportMyData, deleteMyAccount } from "@/lib/privacy.functions";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useTheme } from "@/hooks/use-theme";

export const Route = createFileRoute("/_authenticated/privacy-settings")({
  head: () => ({
    meta: [
      { title: "Privacy Settings — Pluggz" },
      { name: "description", content: "Export your Pluggz data, reset cookie preferences or permanently delete your account." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: PrivacySettings,
});

function PrivacySettings() {
  const exportFn = useServerFn(exportMyData);
  const deleteFn = useServerFn(deleteMyAccount);
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const handleExport = async () => {
    setBusy(true);
    try {
      const data = await exportFn({});
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `plugz-data-export-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Your data has been exported");
    } catch (e: any) {
      toast.error(e.message ?? "Export failed");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (confirm !== "DELETE") return;
    setBusy(true);
    try {
      await deleteFn({ data: { confirm } });
      await supabase.auth.signOut();
      toast.success("Your account has been deleted");
      navigate({ to: "/" });
    } catch (e: any) {
      toast.error(e.message ?? "Deletion failed");
      setBusy(false);
    }
  };

  const handleResetCookies = () => {
    try { localStorage.removeItem("pluggz.cookie-consent"); } catch {}
    toast.success("Cookie preferences reset. Refresh to choose again.");
  };

  return (
    <div className="mx-auto max-w-3xl px-6 py-16">
      <h1 className="font-serif text-4xl">Privacy Settings</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Exercise your rights under GDPR Articles 15, 17 and the UK Data Protection Act.
      </p>

      <section className="mt-10 border border-border p-6" aria-labelledby="display-heading">
        <h2 id="display-heading" className="text-lg font-semibold">Display</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Choose your preferred appearance for the site.
        </p>
        <button
          type="button"
          aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          onClick={toggleTheme}
          className="mt-4 inline-flex items-center gap-2 border border-border px-4 py-2 text-[11px] uppercase tracking-widest hover:bg-card"
        >
          {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          {theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
        </button>
      </section>

      <section className="mt-6 border border-border p-6" aria-labelledby="export-heading">
        <h2 id="export-heading" className="text-lg font-semibold">Export my data</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Download a JSON copy of all personal data we hold about you (Right of Access).
        </p>
        <button
          onClick={handleExport}
          disabled={busy}
          className="mt-4 bg-foreground px-4 py-2 text-[11px] uppercase tracking-widest text-background disabled:opacity-50"
        >
          {busy ? "Working..." : "Download my data"}
        </button>
      </section>

      <section className="mt-6 border border-border p-6" aria-labelledby="cookies-heading">
        <h2 id="cookies-heading" className="text-lg font-semibold">Cookie preferences</h2>
        <p className="mt-1 text-sm text-muted-foreground">Reset your consent choice and review the banner again.</p>
        <button
          onClick={handleResetCookies}
          className="mt-4 border border-border px-4 py-2 text-[11px] uppercase tracking-widest hover:bg-card"
        >
          Reset cookie consent
        </button>
      </section>

      <section className="mt-6 border border-destructive/40 p-6" aria-labelledby="delete-heading">
        <h2 id="delete-heading" className="text-lg font-semibold text-destructive">Delete my account</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Permanently delete your account, profile, creator data, looks, and click history.
          Commission records may be retained where required by tax law. This cannot be undone.
        </p>
        <label htmlFor="confirm" className="mt-4 block text-xs uppercase tracking-widest text-muted-foreground">
          Type DELETE to confirm
        </label>
        <input
          id="confirm"
          type="text"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          className="mt-2 w-64 border border-border bg-background px-3 py-2 text-sm"
          autoComplete="off"
        />
        <div className="mt-4">
          <button
            onClick={handleDelete}
            disabled={busy || confirm !== "DELETE"}
            className="bg-destructive px-4 py-2 text-[11px] uppercase tracking-widest text-destructive-foreground disabled:opacity-50"
          >
            Permanently delete account
          </button>
        </div>
      </section>
    </div>
  );
}
